import Test from '../models/Test';
import INavigationController from './navigation/INavigationController';
import StateController from './state/StateController';
import SessionController from './session/SessionController';
import ResponseController from './response/ResponseController';
import FormController from './form/FormController';
import NavigationTestType from './navigation/NavigationTestType';
import ITestControllerInitializationStrategy from './initialization/ITestControllerInitializationStrategy';
import RegularTestControllerInitializationStrategy from './initialization/RegularTestControllerInitializationStrategy';
import AdaptiveTestControllerInitializationStrategy from './initialization/AdaptiveTestControllerInitializationStrategy';
import PracticeTestInitializationStrategy from './initialization/PracticeTestInitializationStrategy';
import DisplayItem from '@/models/DisplayItem';
import Item from '@/models/Item';
import Section from '@/models/Section';
import PlayerEmitter from '@/event/PlayerEmitter';
import { PauseManager, PauseState, PauseType } from './pause/PauseManager';
import IPauseManagerNotificationObserver from './pause/IPauseManagerNotificationObserver';
import CommandPollingManager from './polling/CommandPollingManager';
import IPauseState from './session/models/IPauseState';
import DeliveryOptionUtility from './options/DeliveryOptionUtility';
import DeliveryOptionConstants from './options/DeliveryOptionConstants';
import PersistenceManager from './persistence/PersistenceManager';
import ScheduledTaskManager from './scheduler/ScheduledTaskManager';
import EventBusConstants from '@/constants/EventBusConstants';
import DeliveryOption from './options/DeliveryOption';
class TestController implements IPauseManagerNotificationObserver {
  private initialized = false;
  private _baseServiceUrl: string | undefined;
  private _formController: FormController | null = null;
  private _sessionController: SessionController | null = null;
  private _responseController: ResponseController | null = null;
  private _stateController: StateController | null = null;
  private _navigationController: INavigationController | null = null;
  private _testType: NavigationTestType = NavigationTestType.REGULAR;
  private _test: Test | null = null;
  private _isPracticeTest = false;
  private readonly _playerEmitter: PlayerEmitter;
  private _pauseManager: PauseManager | null = null;
  private _pollingManager: CommandPollingManager<IPauseState> | undefined;
  private _pauseManagerNotificationObserver: IPauseManagerNotificationObserver | undefined;
  private processing = false;
  private _previewMode = false;
  private _isTestNav = false;
  private _persistenceManager: PersistenceManager | null = null;
  private _jwt: string | undefined = undefined;
  private _fromApp: string | undefined = undefined;
  private _scheduledTaskManager: ScheduledTaskManager | null = null;
  private redirectUrl: string | undefined;

  constructor(baseServiceUrl: string | undefined, playerEmitter: PlayerEmitter) {
    this._baseServiceUrl = baseServiceUrl;
    this._playerEmitter = playerEmitter;
  }

  get playerEmitter(): PlayerEmitter {
    return this._playerEmitter;
  }

  get persistenceManager(): PersistenceManager | null {
    return this._persistenceManager;
  }

  set persistenceManager(value: PersistenceManager | null) {
    this._persistenceManager = value;
  }

  get previewMode(): boolean {
    return this._previewMode;
  }

  get isTestNav(): boolean {
    return this._isTestNav;
  }

  get pauseManagerNotificationObserver(): IPauseManagerNotificationObserver | undefined {
    return this._pauseManagerNotificationObserver;
  }

  set pauseManagerNotificationObserver(value: IPauseManagerNotificationObserver | undefined) {
    this._pauseManagerNotificationObserver = value;
  }

  get baseServiceUrl(): string | undefined {
    return this._baseServiceUrl;
  }

  get getRedirectUrl(): string | undefined {
    return this.redirectUrl;
  }

  get formController(): FormController | null {
    return this._formController;
  }

  set formController(value: FormController | null) {
    this._formController = value;
  }

  get sessionController(): SessionController | null {
    return this._sessionController;
  }

  set sessionController(value: SessionController | null) {
    this._sessionController = value;
  }

  get responseController(): ResponseController | null {
    return this._responseController;
  }

  set responseController(value: ResponseController | null) {
    this._responseController = value;
  }

  get stateController(): StateController | null {
    return this._stateController;
  }

  set stateController(value: StateController | null) {
    this._stateController = value;
  }

  get navigationController(): INavigationController | null {
    return this._navigationController;
  }

  set navigationController(value: INavigationController | null) {
    this._navigationController = value;
  }

  get testType(): NavigationTestType {
    return this._testType;
  }
  get fromApp(): string | undefined {
    return this._fromApp;
  }

  get jwt(): string | undefined {
    return this._jwt;
  }

  get test(): Test | null {
    return this._test;
  }

  set test(value: Test | null) {
    this._test = value;
  }

  get pauseManager(): PauseManager | null {
    return this._pauseManager;
  }

  isPracticeTest(): boolean {
    return this._isPracticeTest;
  }

  getEndTestRedirect(): string {
    if (this._isTestNav) {
      return this.redirectUrl ? this.redirectUrl : this._baseServiceUrl + '/tester?from_app=testnav';
    } else {
      return this.redirectUrl ? this.redirectUrl : this._baseServiceUrl + '/tester';
    }
  }

  deliveryOptionEnabledForTest(key: string): boolean {
    return DeliveryOptionUtility.getDeliveryOptionAndCheckExpectedValue(
      this.test?.getCurrentTestPart().deliveryOptions,
      key,
      'value',
      'true'
    );
  }

  reset(): void {
    this.initialized = false;
    this._test = null;
    this._navigationController = null;
    this._sessionController = null;
    this._formController = null;
    this._stateController = null;
    this._responseController = null;
    this._isPracticeTest = false;
    this._pollingManager?.stop();
    this._pollingManager = undefined;
    this._pauseManager = null;
    this._persistenceManager?.reset();
    this._persistenceManager = null;
    this._scheduledTaskManager = null;
    this._jwt = undefined;
    this._fromApp = undefined;
  }

  initialize(
    qtiSessionId: string | string[] | undefined,
    sessionId: string | undefined,
    formId: string | undefined,
    backend: string | undefined,
    previewMode: boolean | undefined,
    jwt: string | undefined,
    fromApp: string | undefined
  ): Promise<void> {
    let qtiSessionIdValue: string | undefined = undefined;
    if (qtiSessionId) {
      qtiSessionIdValue = typeof qtiSessionId === 'string' ? qtiSessionId : qtiSessionId[0];
    }
    const testControllerInitializationStrategy: ITestControllerInitializationStrategy =
      this.createInitializationStrategy(qtiSessionIdValue);

    if (backend && backend.trim().length > 0) {
      this._baseServiceUrl = backend;
    }
    this._fromApp = fromApp !== undefined ? fromApp : '';
    if (this._fromApp == 'testnav') {
      this._isTestNav = true;
    }
    this._previewMode = previewMode !== undefined ? previewMode : false;
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    return new Promise<void>((resolve, reject) => {
      if (that.initialized) {
        resolve();
        return;
      }
      that.reset();
      that._jwt = jwt;
      testControllerInitializationStrategy
        .initialize(qtiSessionIdValue!, sessionId!, formId!, that)
        .then(() => {
          that._isPracticeTest = testControllerInitializationStrategy instanceof PracticeTestInitializationStrategy;
          const sessionController = that.sessionController;
          const simulate = that._isPracticeTest;
          that._pollingManager = !simulate ? sessionController!.pollingManager : undefined;
          that._pauseManager = new PauseManager(simulate, that._pollingManager, sessionController);

          that._pauseManager.addPauseManagerNotificationObserver(that.pauseManagerNotificationObserver);
          that._pauseManager.addPauseManagerNotificationObserver(that);

          that._pollingManager?.start();
          that.initialized = true;
          that._jwt = undefined;
          that._scheduledTaskManager = new ScheduledTaskManager(that, that.playerEmitter);
          that._pauseManager.addPauseManagerNotificationObserver(that._scheduledTaskManager);

          that._persistenceManager?.addPersistenceEventHandler(that._scheduledTaskManager);
          this.setRedirectUrl();
          resolve();
        })
        .catch((error) => {
          that.reset();
          reject(error);
        });
    });
  }

  private setRedirectUrl(): void {
    const redirectDeliveryOption: DeliveryOption | undefined = DeliveryOptionUtility.getDeliveryOption(
      this.test?.getCurrentTestPart().deliveryOptions,
      DeliveryOptionConstants.REDIRECT_URL
    );
    if (redirectDeliveryOption && redirectDeliveryOption.getSetting('value')) {
      this.redirectUrl = redirectDeliveryOption.getSetting('value');
    }
  }

  setCurrentItem(item: DisplayItem | undefined, oldItem: DisplayItem | undefined): void {
    if (item) {
      item.section.testPart.test.testParts.forEach((testPart) => {
        testPart.sections.forEach((section) => this.clearSectionItems(section));
      });
      if (oldItem) {
        oldItem.currentItem = false;
      }

      item.currentItem = true;
    }
  }

  setItemFlag(item: DisplayItem | undefined): void {
    if (item && item instanceof Item) {
      item.isFlagged = !item.isFlagged;
    }
  }

  releaseSection(): void {
    if (
      this._navigationController &&
      this._navigationController.getCurrentItem() &&
      this._navigationController.getCurrentItem()!.section
    ) {
      this._navigationController.getCurrentItem()!.section.releasedByProctor = true;

      this.sessionController?.itemStateChangedEvent();
      this.persistenceManager?.save(null, true);
    }
  }

  navigationEvent() {
    if (
      this.navigationController?.beginningOfSection() &&
      this.navigationController.getCurrentItem() &&
      this.navigationController.peekPreviousItem()
    ) {
      this.processing = true;
      const currentItem = this.navigationController.getCurrentItem();
      const releasedByProctor = currentItem!.section.releasedByProctor;
      const waitForProctorReleaseSet = DeliveryOptionUtility.getDeliveryOptionAndCheckExpectedValue(
        currentItem?.section.testPart.deliveryOptions,
        DeliveryOptionConstants.PROCTOR_RELEASE,
        'value',
        'true'
      );
      if (waitForProctorReleaseSet && !releasedByProctor) {
        this.pauseManager?.pauseTest(PauseType.SECTION_PAUSE);
      } else {
        this.processing = false;
      }
    }

    this._scheduledTaskManager?.handleEvent();
  }

  get isProcessing(): boolean {
    return this.processing;
  }

  set isProcessing(value: boolean) {
    this.processing = value;
  }

  pauseStateNotification(type: PauseType, state: PauseState) {
    this.processing = false;

    this._playerEmitter.emit(EventBusConstants.PAUSE_EVENT, [['type', state]]);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  pauseError(error: Error) {
    this.processing = false;
  }

  private createInitializationStrategy(qtiSessionId: string | undefined): ITestControllerInitializationStrategy {
    if (this._testType === NavigationTestType.REGULAR) {
      if (qtiSessionId) {
        return new RegularTestControllerInitializationStrategy();
      } else {
        return new PracticeTestInitializationStrategy();
      }
    } else {
      return new AdaptiveTestControllerInitializationStrategy();
    }
  }

  private clearSectionItems(section: Section): void {
    if (section) {
      section.items.forEach((item) => {
        item.currentItem = false;
        if (section.testPart.test.locationState === Test.SECTION_INSTRUCTIONS_STATE) {
          section.testPart.test.locationState = Test.NO_STATE;
        }
      });
      section.subSections.forEach((subSection) => this.clearSectionItems(subSection));
    }
  }
}

export default TestController;
